import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobs } from '../contexts/JobContext';
import { Job } from '../types';
import { DollarSignIcon, TrendingUpIcon, TrendingDownIcon, CalendarIcon, FilterIcon, ArrowLeftIcon } from 'lucide-react';
import { motion } from 'framer-motion';
type TimeFilter = 'week' | 'month' | 'quarter' | 'year' | 'all';
export function AdminAnalytics() {
  const navigate = useNavigate();
  const {
    jobs
  } = useJobs();
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('month');
  const completedJobs = jobs.filter(j => j.status === 'complete' || j.status === 'paid');
  // Calculate totals
  const totalRevenue = completedJobs.reduce((sum, j) => sum + (j.revenue || 0), 0);
  const totalCosts = completedJobs.reduce((sum, j) => sum + j.costs.reduce((s, c) => s + c.amount, 0), 0);
  const totalProfit = totalRevenue - totalCosts;
  const profitMargin = totalRevenue > 0 ? totalProfit / totalRevenue * 100 : 0;
  // Cost breakdown
  const costsByCategory = completedJobs.reduce((acc, job) => {
    job.costs.forEach(cost => {
      acc[cost.category] = (acc[cost.category] || 0) + cost.amount;
    });
    return acc;
  }, {} as Record<string, number>);
  const categoryLabels = {
    fuel: 'Fuel',
    media: 'Blasting Media',
    labor: 'Labor',
    other: 'Other'
  };
  const categoryColors = {
    fuel: 'bg-blue-500',
    media: 'bg-orange-500',
    labor: 'bg-green-500',
    other: 'bg-purple-500'
  };
  return <div className="min-h-screen bg-slate-50 pb-6" data-id="element-184">
      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 pb-8" data-id="element-185">
        <div className="flex items-center gap-4 mb-6" data-id="element-186">
          <motion.button whileTap={{
          scale: 0.9
        }} onClick={() => navigate('/admin')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-187">
            <ArrowLeftIcon className="w-6 h-6" data-id="element-188" />
          </motion.button>
          <div data-id="element-189">
            <h1 className="text-2xl font-bold mb-1" data-id="element-190">Analytics</h1>
            <p className="text-slate-300 text-sm" data-id="element-191">
              Cost management & profit tracking
            </p>
          </div>
        </div>

        {/* Time Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-192">
          {(['week', 'month', 'quarter', 'year', 'all'] as TimeFilter[]).map(filter => <motion.button key={filter} whileTap={{
          scale: 0.95
        }} onClick={() => setTimeFilter(filter)} className={`px-4 py-2 rounded-lg font-semibold text-sm capitalize whitespace-nowrap transition-all ${timeFilter === filter ? 'bg-orange-600 text-white' : 'bg-slate-700 text-slate-300'}`} data-id="element-193">
                {filter === 'all' ? 'All Time' : `This ${filter}`}
              </motion.button>)}
        </div>
      </motion.div>

      <div className="p-6 space-y-6" data-id="element-194">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4" data-id="element-195">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl shadow-md p-5" data-id="element-196">
            <div className="flex items-center gap-2 mb-2" data-id="element-197">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center" data-id="element-198">
                <DollarSignIcon className="w-5 h-5 text-green-600" data-id="element-199" />
              </div>
              <p className="text-sm font-semibold text-slate-600" data-id="element-200">Revenue</p>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-id="element-201">
              ${totalRevenue.toLocaleString()}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.15
        }} className="bg-white rounded-xl shadow-md p-5" data-id="element-202">
            <div className="flex items-center gap-2 mb-2" data-id="element-203">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center" data-id="element-204">
                <TrendingDownIcon className="w-5 h-5 text-red-600" data-id="element-205" />
              </div>
              <p className="text-sm font-semibold text-slate-600" data-id="element-206">Costs</p>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-id="element-207">
              ${totalCosts.toLocaleString()}
            </p>
          </motion.div>
        </div>

        {/* Profit Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-xl p-6 text-white" data-id="element-208">
          <div className="flex items-center gap-3 mb-3" data-id="element-209">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-210">
              <TrendingUpIcon className="w-6 h-6" data-id="element-211" />
            </div>
            <div data-id="element-212">
              <p className="text-orange-100 text-sm font-semibold" data-id="element-213">
                Net Profit
              </p>
              <p className="text-xs text-orange-200" data-id="element-214">
                {completedJobs.length} completed jobs
              </p>
            </div>
          </div>
          <p className="text-4xl font-bold mb-2" data-id="element-215">
            ${totalProfit.toLocaleString()}
          </p>
          <div className="flex items-center gap-4 text-sm text-orange-100" data-id="element-216">
            <span data-id="element-217">Margin: {profitMargin.toFixed(1)}%</span>
            <span data-id="element-218">•</span>
            <span data-id="element-219">
              Avg: $
              {completedJobs.length > 0 ? (totalProfit / completedJobs.length).toFixed(0) : 0}
              /job
            </span>
          </div>
        </motion.div>

        {/* Cost Breakdown */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="bg-white rounded-xl shadow-md p-6" data-id="element-220">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-221">Cost Breakdown</h3>
          <div className="space-y-4" data-id="element-222">
            {Object.entries(costsByCategory).map(([category, amount], index) => {
            const percentage = totalCosts > 0 ? amount / totalCosts * 100 : 0;
            return <motion.div key={category} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3 + index * 0.05
            }} data-id="element-223">
                    <div className="flex items-center justify-between mb-2" data-id="element-224">
                      <span className="text-sm font-semibold text-slate-700" data-id="element-225">
                        {categoryLabels[category as keyof typeof categoryLabels]}
                      </span>
                      <span className="text-sm font-bold text-slate-900" data-id="element-226">
                        ${amount.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-227">
                      <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${percentage}%`
                }} transition={{
                  delay: 0.4 + index * 0.05,
                  duration: 0.6,
                  ease: 'easeOut'
                }} className={`h-full ${categoryColors[category as keyof typeof categoryColors]} rounded-full`} data-id="element-228" />
                    </div>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-229">
                      {percentage.toFixed(1)}% of total costs
                    </p>
                  </motion.div>;
          })}
          </div>
        </motion.div>

        {/* Completed Jobs List */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-xl shadow-md p-6" data-id="element-230">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-231">Completed Jobs</h3>
          <div className="space-y-3" data-id="element-232">
            {completedJobs.map((job, index) => {
            const jobCosts = job.costs.reduce((sum, c) => sum + c.amount, 0);
            const jobProfit = (job.revenue || 0) - jobCosts;
            const jobMargin = job.revenue ? jobProfit / job.revenue * 100 : 0;
            return <motion.div key={job.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.35 + index * 0.05
            }} whileTap={{
              scale: 0.98
            }} onClick={() => navigate(`/admin/job/${job.id}`)} className="p-4 bg-slate-50 rounded-lg border-2 border-slate-100 hover:border-orange-200 transition-colors cursor-pointer" data-id="element-233">
                  <div className="flex items-start justify-between mb-2" data-id="element-234">
                    <div className="flex-1" data-id="element-235">
                      <h4 className="font-bold text-slate-900 mb-1" data-id="element-236">
                        {job.title}
                      </h4>
                      <p className="text-xs text-slate-500" data-id="element-237">
                        {job.customerName}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-238">
                      <p className={`text-lg font-bold ${jobProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-239">
                        ${jobProfit.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-240">
                        {jobMargin.toFixed(1)}% margin
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-slate-600" data-id="element-241">
                    <span data-id="element-242">Revenue: ${(job.revenue || 0).toLocaleString()}</span>
                    <span data-id="element-243">•</span>
                    <span data-id="element-244">Costs: ${jobCosts.toLocaleString()}</span>
                  </div>

                  {job.completedDate && <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500" data-id="element-245">
                      <CalendarIcon className="w-3 h-3" data-id="element-246" />
                      <span data-id="element-247">
                        Completed{' '}
                        {new Date(job.completedDate).toLocaleDateString()}
                      </span>
                    </div>}
                </motion.div>;
          })}

            {completedJobs.length === 0 && <div className="text-center py-8" data-id="element-248">
                <p className="text-slate-500" data-id="element-249">No completed jobs yet</p>
              </div>}
          </div>
        </motion.div>
      </div>
    </div>;
}